import { supabaseClient, useUser } from '@auth-v2';
import { useQuery, useMutation, useInfiniteQuery, InfiniteData } from '@tanstack/react-query';

import {
    getPaginatedNotifications,
    getUINotification,
    Notification,
    markNotificationsAsRead,
    NotificationUiModel,
    NOTIFICATION_PAGE_SIZE,
    getThreadMessagesUnreadNotifications,
} from '@data';

import { queryClient } from '../config';

// cache key for notification entity the unenriched type
export const getNotificationsKey = (sourceUserId: string) => ['notifications', sourceUserId] as const;
// address all the keys
export const getBaseUiNotificationKey = (sourceUserId: string) => ['notifications', sourceUserId, 'ui'] as const;
// Used to address one UI notification in the cache
export const getUiNotificationKey = (sourceUserId: string, notificationId: number) =>
    ['notifications', sourceUserId, 'ui', notificationId] as const;
// used to access unread notification ds
export const getThreadMessageNotificationsKey = () => ['thread-message-notification-maps'] as const;

export const useThreadMessageUnreadNotifications = () => {
    const { data: user } = useUser();

    return useQuery({
        queryKey: getThreadMessageNotificationsKey(),
        queryFn: () => getThreadMessagesUnreadNotifications(supabaseClient),
        enabled: !!user?.id,
        refetchOnMount: false,
    });
};

export const usePaginatedNotifications = (sourceUserId: string) => {
    const { data: user } = useUser();
    return useInfiniteQuery({
        queryKey: getNotificationsKey(sourceUserId),
        queryFn: ({ pageParam = 0 }) => getPaginatedNotifications(supabaseClient, pageParam, sourceUserId),
        initialPageParam: 0,
        getNextPageParam: (lastPage, allPages) => {
            return lastPage.length === NOTIFICATION_PAGE_SIZE ? allPages.length : undefined;
        },
        enabled: !!user?.id,
    });
};

export const useMarkNotificationAsRead = (sourceUserId: string) => {
    return useMutation({
        mutationFn: (notificationIds?: number[]) => markNotificationsAsRead(supabaseClient, notificationIds),
        onSuccess: (_, notificationIds?: number[]) => {
            queryClient.invalidateQueries({ queryKey: getThreadMessageNotificationsKey() });
            // we did not receive notificationIds that means we need to
            // mark all notifications as read
            if (!notificationIds) {
                markAllAsRead(sourceUserId);
                return;
            }
            // mark notifications as read by ids
            notificationIds.forEach((notificationId) => {
                invalidateCacheForOneNotification(notificationId, sourceUserId);
            });
        },
    });
};

/**
 * This function joins notifications with swing thread one at a time when we are lazy loading the list
 * we send in a notification model into the function and get a enriched model out - for now only thread message notification
 */
export const useUiNotification = (notification: Notification) => {
    const { data: user } = useUser();

    return useQuery({
        queryKey: getUiNotificationKey(notification.sourceUserId ?? '', notification.id),
        queryFn: () => getUINotification(supabaseClient, notification),
        refetchOnMount: true,
        enabled: !!user?.id && !!notification && !!notification.sourceUserId,
    });
};

////////////////////////////////////////////////////////////////////////////////////
// Util Functions for invalidating the cache after we set entity/entities to read //
////////////////////////////////////////////////////////////////////////////////////
const markAllAsRead = (sourceUserId: string) => {
    // use the base cache key to get all of the ui notifications and set them to read
    const uiModels = queryClient.getQueriesData({ queryKey: getBaseUiNotificationKey(sourceUserId) });
    uiModels.forEach(([key, uiModel]) => {
        if (uiModel) {
            queryClient.setQueryData(key, {
                ...uiModel,
                readAt: new Date(),
            });
        }
    });
    // The tricky typing here is caused by react query lazy load DS
    queryClient.setQueryData(
        getNotificationsKey(sourceUserId),
        (notificationPages: InfiniteData<Notification[]> | undefined) => {
            if (!notificationPages) return notificationPages;
            const setAllNotificationsToRead = (notifications: Notification[]) =>
                notifications.map((n) => ({
                    ...n,
                    readAt: new Date().toISOString(),
                }));
            return {
                ...notificationPages,
                pages: notificationPages.pages.map(setAllNotificationsToRead),
            };
        },
    );
};

const invalidateCacheForOneNotification = (notificationId: number, sourceUserId: string) => {
    // Use the query key to get one ui notification to read
    queryClient.setQueryData(
        getUiNotificationKey(sourceUserId, notificationId),
        (oldNotificationUiModel: NotificationUiModel) => {
            if (!oldNotificationUiModel) return oldNotificationUiModel;
            return { ...oldNotificationUiModel, readAt: new Date(Date.now()) };
        },
    );
    // Reset one paginated notifications
    queryClient.setQueryData<InfiniteData<Notification[]>>(getNotificationsKey(sourceUserId), (notification) => {
        if (!notification) return notification;

        const notificationMap = (notification: Notification) =>
            notification.id === notificationId ? { ...notification, readAt: new Date().toISOString() } : notification;

        return {
            ...notification,
            pages: notification.pages.map((page) => page.map(notificationMap)),
        };
    });
};
